import { useOrganization } from '../contexts/OrganizationContext';

type UserRole = 'owner' | 'admin' | 'member' | 'viewer';

/**
 * Permission string constants. These MUST stay in sync with the backend
 * `Permission` enum in SEO-SaaS/services/auth/auth_models.py — the values are
 * identical ("org:update", etc.) so permissions embedded in the access-token
 * JWT can be compared directly without translation.
 */
const Permission = {
  ORG_CREATE: 'org:create',
  ORG_READ: 'org:read',
  ORG_UPDATE: 'org:update',
  ORG_DELETE: 'org:delete',
  ORG_INVITE: 'org:invite',
  ORG_REMOVE_MEMBER: 'org:remove_member',
  ORG_UPDATE_MEMBER: 'org:update_member',
  ORG_VIEW_AUDIT_LOG: 'org:view_audit_log',
} as const;

type Permission = typeof Permission[keyof typeof Permission];

/**
 * Role → permissions map mirroring RolePermissions.get_role_permissions on the
 * backend (organization-scoped subset). Keep synchronized with
 * SEO-SaaS/services/auth/auth_models.py::RolePermissions.
 */
const ROLE_PERMISSIONS: Record<UserRole, Permission[]> = {
  owner: [
    Permission.ORG_CREATE,
    Permission.ORG_READ,
    Permission.ORG_UPDATE,
    Permission.ORG_DELETE,
    Permission.ORG_INVITE,
    Permission.ORG_REMOVE_MEMBER,
    Permission.ORG_UPDATE_MEMBER,
    Permission.ORG_VIEW_AUDIT_LOG,
  ],
  admin: [
    Permission.ORG_READ,
    Permission.ORG_UPDATE,
    Permission.ORG_INVITE,
    Permission.ORG_REMOVE_MEMBER,
    Permission.ORG_UPDATE_MEMBER,
    Permission.ORG_VIEW_AUDIT_LOG,
  ],
  member: [Permission.ORG_READ],
  viewer: [Permission.ORG_READ],
};

export const usePermissions = () => {
  const { currentOrganization } = useOrganization();

  const hasPermission = (permission: Permission): boolean => {
    if (!currentOrganization) return false;

    const role = (currentOrganization as any).user_role as UserRole | undefined;
    if (!role) return false;

    return ROLE_PERMISSIONS[role]?.includes(permission) || false;
  };

  const canInviteMembers = () => hasPermission(Permission.ORG_INVITE);
  const canManageOrganization = () => hasPermission(Permission.ORG_UPDATE);
  const canRemoveMembers = () => hasPermission(Permission.ORG_REMOVE_MEMBER);
  const canDeleteOrganization = () => hasPermission(Permission.ORG_DELETE);
  const canUpdateMemberRoles = () => hasPermission(Permission.ORG_UPDATE_MEMBER);
  const canViewAuditLog = () => hasPermission(Permission.ORG_VIEW_AUDIT_LOG);

  const currentRole = currentOrganization ? (currentOrganization as any).user_role as UserRole : null;

  return {
    hasPermission,
    canInviteMembers,
    canManageOrganization,
    canRemoveMembers,
    canDeleteOrganization,
    canUpdateMemberRoles,
    canViewAuditLog,
    currentRole,
  };
};

export { Permission };
export type { UserRole };

